import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  TexasRangersAdditionalDetailsDisclaimerTC,
  TexasRangersCheckoutTerms,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BackgroundImage from "public/images/texasrangers/rangersstadium.jpg";
import LogoImage from "public/images/texasrangers/T_Primary.png";
import StaticSeatMap from "public/images/texasrangers/seat-map.png";

const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/2024-25/25_1486x148_Suites.jpg";

const config: TeamConfig = {
  teamSchema: "texasrangers_sandbox",
  browserTitle: "Texas Rangers",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 140,
  teamName: "Texas Rangers",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00084-rangers-sec",
    mapId: "main",

    suiteImages: [
      "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/Party/201-202/2020_Suite_GAVN3709.jpeg",
      "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/Party/201-202/2020_Suite_GAVN3760.jpeg",
      "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/Party/201-202/2020_Suite_GAVN3814.jpeg",
      "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/Party/201-202/2020_Suite_GAVN3841.jpeg",
      "https://ss-leagues.s3.amazonaws.com/MLB/TX%20Rangers%20Suites/Party/202/2020_Suite_GAVN3697%20Large.jpeg",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.mlb.com/rangers/tickets",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://integrator-3404041.okta.com", // dev okta creds
  oktaClientId: "0oayigvuniVNS5aTO697", // dev okta creds
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    findGamesBannerImage: BannerImage,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },

    hidePackageBalanceOnCheckout: true,
    hideServiceFee: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: [
        { suiteId: "Hall of Fame Suite 201A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 201B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 202A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 202B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 203A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 203B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 204A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 204B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 205A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 205B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 206A", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 206B", FBPackageValue: "$600 Credit" },
        { suiteId: "Hall of Fame Suite 207A", FBPackageValue: "$600 Credit" },
        { suiteId: "Classic Suite 201", FBPackageValue: "$600 Credit" },
        { suiteId: "Classic Suite 202", FBPackageValue: "$600 Credit" },
        { suiteId: "Classic Suite 242", FBPackageValue: "$600 Credit" },
        { suiteId: "Classic Suite 243", FBPackageValue: "$600 Credit" },
        { suiteId: "Party Suite 201", FBPackageValue: "Package" },
        { suiteId: "Party Suite 202", FBPackageValue: "Package" },
        { suiteId: "Party Suite 103", FBPackageValue: "Package" },
        { suiteId: "Party Suite 104", FBPackageValue: "Package" },
      ],
      pbLineItemsValue: 5,
      FBLabel: "F&B Included:",
      isParkingSeatCountVisible: true,
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },
  },

  colors: {
    primary: "#003278",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <TexasRangersCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: (
      <TexasRangersAdditionalDetailsDisclaimerTC />
    ),
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your reservation is not yet complete until notification from the Texas Rangers confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
